import type { Revision, RevisionReusableContent } from '../../types';
import { addUserEdits, type Edits, isYValue, jsonToY, mergeEdits, mutations } from '../../utils';

/**
 * Get reusable content in a revision by its id.
 */
export function getReusableContentInRevision(
    revision: mutations.MutableData<Revision>,
    reusableContentId: string
): mutations.MutableData<RevisionReusableContent> | undefined {
    return mutations.getIn(revision, ['reusableContents', reusableContentId] as const);
}

/**
 * Insert a reusable content in a revision.
 */
export function insertReusableContentInRevision(
    revision: mutations.MutableData<Revision>,
    reusableContent: RevisionReusableContent
): void {
    const existing = mutations.getIn(revision, ['reusableContents', reusableContent.id] as const);
    if (existing) {
        throw new Error('Reusable content with same ID already exists in revision');
    }
    const reusableContents = mutations.get(revision, 'reusableContents');
    mutations.set(reusableContents, reusableContent.id, reusableContent);
}

/**
 * Remove a reusable content from a revision.
 */
export function removeReusableContentInRevision(
    revision: mutations.MutableData<Revision>,
    reusableContentId: string
): void {
    mutations.delIn(revision, ['reusableContents', reusableContentId] as const);
}

/**
 * Update the reusable content in a revision.
 */
export function updateReusableContentInRevision(
    revision: mutations.MutableData<Revision>,
    reusableContentId: string,
    update: Partial<Omit<RevisionReusableContent, 'id'>>
): void {
    const existing = mutations.getIn(revision, ['reusableContents', reusableContentId] as const);
    if (!existing) {
        throw new Error(`Reusable content doesn't exist in revision`);
    }
    Object.entries(update).forEach(([key, value]) => {
        mutations.set(
            existing,
            // @ts-ignore
            key,
            // @ts-ignore
            isYValue(revision) ? jsonToY(value) : value
        );
    });
}

/**
 * Mark a page as edited by a user.
 */
export function markReusableContentAsEdited(
    revision: mutations.MutableData<Revision>,
    reusableContentId: string,
    userId: string | string[] | mutations.MutableData<Edits>
) {
    const yEdits = mutations.getIn(revision, [
        'reusableContents',
        reusableContentId,
        'edits',
    ] as const);

    if (!yEdits) {
        return;
    }

    const result = (() => {
        if (Array.isArray(userId)) {
            return addUserEdits(yEdits, userId);
        }
        if (typeof userId === 'string') {
            return addUserEdits(yEdits, [userId]);
        }
        return mergeEdits(yEdits, userId);
    })();

    mutations.setIn(revision, ['reusableContents', reusableContentId, 'edits'] as const, result);
}
